import { useState, useRef, useEffect } from 'react';

const useMouseShadow = (shadowType = 'textShadow') => {
  const [shadow, setShadow] = useState({ x: 0, y: 0 });
  const textRef = useRef();

  useEffect(() => {
    // Detect if device is mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      || (window.innerWidth <= 768);

    if (isMobile) {
      // Mobile: Use gyroscope (DeviceOrientation API)
      const handleOrientation = (event) => {
        if (!textRef.current) {
          return;
        }

        // beta: front-to-back tilt in degrees (-180 to 180)
        // gamma: left-to-right tilt in degrees (-90 to 90)
        const beta = event.beta || 0;   // Vertical tilt
        const gamma = event.gamma || 0; // Horizontal tilt

        // Normalize the values and invert for natural shadow effect
        // Increased sensitivity by dividing by smaller numbers
        const rawX = gamma / 2;   // More dramatic effect
        const rawY = (beta - 45) / 2; // Offset beta by 45 to account for typical phone holding angle

        // Clamp values to prevent extreme shadows
        const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
        const x = clamp(rawX, -20, 20);
        const y = clamp(rawY, -20, 20);

        setShadow({ x, y });
      };

      // Request permission for iOS 13+ devices
      if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
        // For iOS, we need user interaction to request permission
        const requestPermission = () => {
          DeviceOrientationEvent.requestPermission()
            .then(permissionState => {
              if (permissionState === 'granted') {
                window.addEventListener('deviceorientation', handleOrientation);
              }
            })
            .catch(console.error);
        };

        // Add a click listener to request permission on first interaction
        const clickHandler = () => {
          requestPermission();
          document.removeEventListener('click', clickHandler);
          document.removeEventListener('touchstart', clickHandler);
        };

        document.addEventListener('click', clickHandler);
        document.addEventListener('touchstart', clickHandler);

        return () => {
          window.removeEventListener('deviceorientation', handleOrientation);
          document.removeEventListener('click', clickHandler);
          document.removeEventListener('touchstart', clickHandler);
        };
      } else {
        // Non-iOS or older iOS devices
        window.addEventListener('deviceorientation', handleOrientation);

        return () => {
          window.removeEventListener('deviceorientation', handleOrientation);
        };
      }
    } else {
      // Desktop: Use mouse position
      const handleMouseMove = (event) => {
        if (!textRef.current) {
          return;
        }
        const textRect = textRef.current.getBoundingClientRect();
        const x = (textRect.left + textRect.right) / 2;
        const y = (textRect.top + textRect.bottom) / 2;
        setShadow({ x: (x - event.clientX) / 25, y: (y - event.clientY) / 25 });
      };

      document.addEventListener('mousemove', handleMouseMove);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, []);

  const style = {
    [shadowType]: `${shadow.x}px ${shadow.y}px 3px rgba(0,0,0,0.3)`
  };

  return [style, textRef];
};

export default useMouseShadow;